import type { ReactNode } from 'react'
import type { AreaUnit, CanadaGapFarmFormKey, CanadaGapFormKey, Farm, Field } from '../lib/types'
import type { Values } from './use-draft'

/** What a step (or a flow's commit) can use. `last` is the most recent entry of this kind, if any. */
export type StepCtx = {
  orgId: string
  /** The farm the entry is for ('' until chosen, when the wizard was opened without one). */
  farmId: string
  farms: Farm[]
  /** The address already names the farm, so the first step doesn't ask. */
  farmFixed: boolean
  setFarm: (farmId: string) => void
  fields: Field[]
  /**
   * The field this entry is for. In a flow that covers several fields it is set only while one
   * field's log is being built (`entries`, `warnings`); on the steps it is the first chosen field.
   */
  field: Field | undefined
  /** Every field chosen on the first step, in the farm's order ([] for a farm-level log). */
  selectedFields: Field[]
  areaUnit: AreaUnit
  values: Values
  patch: (partial: Values) => void
  last: unknown
}

export type WizardStep = {
  id: string
  title: string
  /**
   * Next stays disabled until the step is valid. Back and leaving are always allowed. `ctx` is given
   * by the wizard, for a step whose answer depends on the chosen fields (amounts from their areas).
   */
  valid: (values: Values, ctx?: StepCtx) => boolean
  render: (ctx: StepCtx) => ReactNode
}

export type ReviewLine = { label: string; value: string }

/** A display-only form badge, shown on the review step. Never stored on the data. */
export type FormBadge = { framework: string; formCode: string; formName: string }

/** One log a wizard entry records: a body for the create endpoint of `kind`. */
export type FlowEntry = {
  kind: string
  body: Record<string, unknown>
  /** The CanadaGAP form this log fills (checked on review), or none: scouting has no form. */
  checkForm?: CanadaGapFormKey | CanadaGapFarmFormKey
  badge?: FormBadge
  /** Set for a field-level log: the review groups checks by field, and "See it" links there. */
  fieldId?: string
  historyPath: string
}

/** One kind of entry the wizard can log. Adding a log type is one of these plus its steps. */
export type FlowDef = {
  kind: string
  label: string
  /**
   * The CanadaGAP form this entry fills, if any. The review step checks each `entry()` against it,
   * the same check the Add/Edit dialogs run, so the wizard and the dialogs can't disagree about what
   * is missing.
   */
  checkForm?: CanadaGapFormKey | CanadaGapFarmFormKey
  /** Asks which field first (spray, fertilizer, amendment, harvest). Farm-level logs only ask the date. */
  needsField: boolean
  /**
   * The grower may tick several fields: one wizard entry then records one log per field (CanadaGAP
   * records and pre-harvest intervals are per field). Chosen fields are `values.fieldIds`; values a
   * grower sets for one field only are in `values.perField[fieldId]` and override the shared ones.
   */
  severalFields?: boolean
  /** Starting values; `today` is the farm-local ISO date. */
  initial: (seed: { today: string; fieldId?: string }) => Values
  /** The most recent entry of this kind, for "Same as last time". Called when the field changes. */
  fetchLast?: (orgId: string, farmId: string, fieldId: string | undefined) => Promise<unknown>
  steps: WizardStep[]
  review: (ctx: StepCtx) => { title: string; lines: ReviewLine[] }
  /**
   * Problems shown on the review step (for example a harvest inside a pre-harvest interval). They
   * never stop the entry being recorded: something that happened must still be logged. The grower
   * acknowledges them before finishing, and the record's CanadaGAP status keeps flagging them.
   */
  warnings?: (ctx: StepCtx) => Promise<string[]>
  /**
   * The body for this kind's create endpoint, for one field (`ctx.field` and `values.fieldId` name
   * it). The wizard sends every entry with the draft to `POST /log-drafts/:id/commit`, which creates
   * them all and retires the draft in one step.
   */
  entry: (ctx: StepCtx) => Record<string, unknown>
  /**
   * For a flow whose logs are of other kinds than its own (crop inputs: spray and fertilizer logs),
   * the logs for one field. Without it, one `entry()` of the flow's own kind.
   */
  entries?: (ctx: StepCtx) => FlowEntry[]
  /** Display-only: the form this entry belongs to, shown on the review step. Never stored on the data. */
  badge?: FormBadge
  historyPath: (farmId: string, fieldId: string | undefined) => string
  /** Runs once the entry is recorded (best effort): e.g. puts new names on the saved people list. */
  onRecorded?: (ctx: StepCtx) => Promise<void>
  /** What to offer once the entry is recorded, besides "Log something else" (scouting: Lookup links). */
  after?: (ctx: StepCtx) => { label: string; href: string }[]
}

/** The fields an entry is for. Drafts from before several fields could be chosen have one `fieldId`. */
export function fieldIdsOf(values: Values): string[] {
  if (Array.isArray(values.fieldIds)) return values.fieldIds.filter((id): id is string => typeof id === 'string' && id !== '')
  return typeof values.fieldId === 'string' && values.fieldId ? [values.fieldId] : []
}

/** Values set for one field only (`values.perField`). */
export function fieldValues(values: Values, fieldId: string): Values {
  const all = values.perField
  const own = all && typeof all === 'object' ? (all as Record<string, unknown>)[fieldId] : undefined
  return own && typeof own === 'object' ? (own as Values) : {}
}

/** Sets values for one field only. */
export function patchField(ctx: Pick<StepCtx, 'values' | 'patch'>, fieldId: string, partial: Values) {
  const all = ctx.values.perField && typeof ctx.values.perField === 'object' ? (ctx.values.perField as Record<string, Values>) : {}
  ctx.patch({ perField: { ...all, [fieldId]: { ...fieldValues(ctx.values, fieldId), ...partial } } })
}

/**
 * The context for one field's log: that field, and the shared values with the field's own on top.
 * Patches from it land in that field's values.
 */
export function forField(ctx: StepCtx, field: Field): StepCtx {
  return {
    ...ctx,
    field,
    values: { ...ctx.values, ...fieldValues(ctx.values, field.id), fieldId: field.id },
    patch: (partial) => patchField(ctx, field.id, partial),
  }
}

/** Every log the entry records: for each chosen field (or once, for a farm-level log). */
export function entriesOf(flow: FlowDef, ctx: StepCtx): FlowEntry[] {
  const one = (c: StepCtx): FlowEntry[] =>
    flow.entries
      ? flow.entries(c)
      : [{ kind: flow.kind, body: flow.entry(c), checkForm: flow.checkForm, badge: flow.badge, fieldId: c.field?.id, historyPath: flow.historyPath(c.farmId, c.field?.id) }]
  if (!flow.needsField) return one(ctx)
  return ctx.selectedFields.flatMap((f) => one(forField(ctx, f)))
}

/**
 * Today's ISO date on the farm's clock (its IANA `timezone`), so an entry made late in the evening
 * from another timezone still lands on the farm's day. Without a timezone, this device's clock.
 */
export function todayIso(timeZone?: string, now = new Date()): string {
  if (timeZone) {
    try {
      // en-CA formats as YYYY-MM-DD.
      return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(now)
    } catch {
      // Unknown zone: fall through to the device's.
    }
  }
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

export const seasonYearOf = (isoDate: string) => Number(isoDate.slice(0, 4))

/** ISO date plus whole days (UTC, so the day never shifts). */
export function addDays(isoDate: string, days: number): string {
  const d = new Date(`${isoDate}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}

/** The largest amount or rate a log can store (quantities and rates are numeric(10, 4) on the server). */
export const MAX_AMOUNT = 999_999.9999

/**
 * Why a typed amount can't be saved, or null. A blank is not a problem here: whether the amount is
 * required is the step's call.
 */
export function amountProblem(v: unknown): string | null {
  const s = (typeof v === 'string' ? v : v == null ? '' : String(v)).trim()
  if (s === '') return null
  const n = Number(s)
  if (Number.isNaN(n)) return 'Enter a number.'
  if (n <= 0) return 'Enter an amount above zero.'
  if (n > MAX_AMOUNT) return 'That’s more than a log can hold (up to 999,999). Check the number, or use a bigger unit.'
  return null
}

export const str = (v: unknown) => (typeof v === 'string' ? v : '')
export const optText = (v: unknown) => str(v).trim() || undefined
export const optNum = (v: unknown) => {
  const s = str(v).trim()
  return s === '' || Number.isNaN(Number(s)) ? undefined : Number(s)
}
